// Where a copied object lands when you paste it.
//
// Copy/paste is a placement problem, not a clipboard problem: the hard part
// is not remembering what was copied, it is deciding where it may legally go
// given one world point the user clicked. That decision is pure — a payload,
// an anchor and a document in, one existing `EditorAction` out — so it lives
// here rather than in the keyboard handler, and it is the same decision on
// web, on mobile, and in a test.
//
// Two rules, from the feature's spec:
//
//   • A WALL-ATTACHED object may only be pasted onto a surface. The anchor is
//     the last point the user touched a surface at, so pasting always lands
//     somewhere they actually pointed rather than at a default the room
//     picked for them.
//   • A FLOOR object lands at the anchor's ground position. If the last thing
//     touched was a wall, that is the wall's foot — `clampBoxToRoom` pulls the
//     footprint just inside the polygon, so "click the wall, paste the sofa"
//     puts the sofa against that wall, which is what the gesture looks like it
//     should do.
//
// Nothing here mutates and nothing here dispatches. `resolvePaste` returns an
// action the caller hands to the reducer unchanged — so paste inherits undo,
// staleness classification and the read-only guard from the actions that
// already exist, and adds no reducer surface of its own.

import * as THREE from 'three';
import type {
  EditorAction, EditorDoc, FurnitureEntry, MediaEntry, PanelEntry,
  PanelSize, SpeakerEntry, Vec3, WindowEntry,
} from '@roomtreatment/core';
import { faceToward, mediaAsMountable } from '@roomtreatment/core';
import {
  clampPose, findSurface, nearestCornerFrame, parseSurfaceKey, poseToTransform,
  roomSurfaces, surfaceKey, transformToPose,
  type SurfaceFrame, type SurfaceRef,
} from './panelSurface';
import { getRoomPolygon } from './roomPolygon';
import { clampBoxToRoom } from './polygonClamp';
import { footprintHalfXZ } from './footprint';
import { mediaHostSurface, mediaSurfaceOptions } from './mediaSurface';
import { clampWindowOpening, openingsOverlap } from './windowOpening';

/** How far each repeat paste at the SAME anchor steps, metres.
 *
 *  Diagonal, not along one axis: a single-axis cascade that runs into a wall
 *  edge gets pinned there by `clampPose`, and every further paste then lands
 *  on the same pinned spot — which is the stacking this exists to prevent.
 *  Offsetting both axes means a clamp on one still leaves the other
 *  separating the copies. */
export const PASTE_CASCADE_M = 0.15;

/**
 * A copied object, detached from the document it came from.
 *
 * Deliberately a SNAPSHOT of the entry's fields, never its id. Three things
 * break an id-based clipboard, and all three are reachable in normal use:
 * clicking a wall to choose where to paste CLEARS the selection (see
 * SELECT_SURFACE in editorState), deleting the original before pasting leaves
 * the id resolving to nothing, and pasting into another Design is a different
 * document where the id was never valid.
 *
 * `footprint` is carried rather than derived because a speaker has no `size`
 * of its own — its footprint is a rendering constant that lives in
 * `@roomtreatment/viewport-3d`, which depends on THIS package and so can
 * never be imported from it. Capturing it at copy time keeps the resolver
 * pure and the dependency graph acyclic.
 */
export type ClipboardPayload =
  | { kind: 'panel'; entry: Omit<PanelEntry, 'id'>; footprint: PanelSize; spin: number }
  | { kind: 'media'; entry: Omit<MediaEntry, 'id'>; footprint: PanelSize; spin: number }
  | { kind: 'furniture'; entry: Omit<FurnitureEntry, 'id'>; footprint: PanelSize }
  | { kind: 'speaker'; entry: Omit<SpeakerEntry, 'id'>; footprint: PanelSize }
  | { kind: 'window'; entry: Omit<WindowEntry, 'id'>; footprint: PanelSize };

/** The last surface point the user touched — where a paste goes. */
export interface PasteAnchor {
  /** Which surface was hit. Re-resolved against the live document on every
   *  paste: `SurfaceRef.index` is a room-POLYGON EDGE index, and reshaping the
   *  room renumbers those, so a stored ref can silently come to mean a
   *  different wall. */
  ref: SurfaceRef;
  /** World-space hit point. */
  point: Vec3;
}

export type PasteRefusal =
  /** Nothing has been copied. */
  | 'empty'
  /** No surface has been clicked yet, so there is nowhere to put it. */
  | 'no-anchor'
  /** The anchored surface no longer exists — the room was reshaped. */
  | 'surface-gone'
  /** This kind of object cannot mount on that kind of surface. */
  | 'wrong-surface'
  /** A window would land on top of another opening. */
  | 'overlap';

export type PasteResult =
  | { ok: true; action: EditorAction }
  | { ok: false; reason: PasteRefusal };

const refuse = (reason: PasteRefusal): PasteResult => ({ ok: false, reason });

/** True when this payload is glued to a mounting surface rather than free. */
export function isSurfaceBound(payload: ClipboardPayload): boolean {
  switch (payload.kind) {
    case 'window':
      return true;
    case 'panel':
    case 'media':
      return !payload.entry.detached;
    default:
      return false;
  }
}

/**
 * Which surface a payload may mount on, given where the user clicked.
 *
 * Not simply "the surface that was clicked": a corner trap straddles a
 * vertical corner and can never sit on a flat wall, so a corner-shaped panel
 * resolves to the corner NEAREST the click instead. That keeps the gesture
 * working — clicking the wall beside a corner still pastes the trap into the
 * corner — rather than refusing a click that clearly meant "over there".
 */
export function resolvePasteSurface(
  payload: ClipboardPayload, anchor: PasteAnchor, surfaces: SurfaceFrame[],
): SurfaceFrame | null {
  if (payload.kind === 'panel' && payload.entry.shape === 'corner') {
    return nearestCornerFrame(surfaces, anchor.point[0], anchor.point[2]);
  }
  const clicked = findSurface(surfaces, anchor.ref);
  if (!clicked) return null;
  if (payload.kind === 'media') {
    // A TV hangs on a wall, a projector from the ceiling. Nothing else.
    const allowed = mediaSurfaceOptions(payload.entry.kind, surfaces);
    return allowed.some((s) => surfaceKey(s.ref) === surfaceKey(clicked.ref)) ? clicked : null;
  }
  if (payload.kind === 'window') {
    return clicked.ref.kind === 'wall' ? clicked : null;
  }
  // A flat panel mounts on a wall, ceiling or floor — never straddling a
  // corner, which is a different shape of object entirely.
  return clicked.ref.kind === 'corner' ? null : clicked;
}

/**
 * The action that pastes `payload` at `anchor`, or why it cannot.
 *
 * `repeat` is how many pastes have already landed at this anchor without it
 * moving; it drives the cascade offset so Ctrl+V three times gives three
 * visible copies instead of three coincident ones.
 */
export function resolvePaste(
  payload: ClipboardPayload | null,
  anchor: PasteAnchor | null,
  doc: EditorDoc,
  repeat = 0,
): PasteResult {
  if (!payload) return refuse('empty');
  if (!anchor) return refuse('no-anchor');

  const surfaces = roomSurfaces(doc);
  const step = repeat * PASTE_CASCADE_M;

  if (!isSurfaceBound(payload)) return pasteOnGround(payload, anchor, doc, step);

  const frame = resolvePasteSurface(payload, anchor, surfaces);
  if (!frame) {
    // Tell the two apart: a ref that no longer resolves means the ROOM
    // changed under a stale anchor, which is a different thing to explain
    // than "that object cannot go there".
    return refuse(findSurface(surfaces, anchor.ref) ? 'wrong-surface' : 'surface-gone');
  }

  if (payload.kind === 'window') return pasteWindow(payload, frame, anchor, doc, step);
  if (payload.kind === 'panel' || payload.kind === 'media') {
    return pasteOnSurface(payload, frame, anchor, doc, step);
  }
  return refuse('wrong-surface');
}

/** Project the anchor onto `frame` and seat the object there.
 *
 *  `spin` comes off the CLIPBOARD, not from re-deriving it out of the stored
 *  world rotation: that rotation only means anything relative to the surface
 *  the object was copied FROM, so re-deriving it against a different wall
 *  silently rotates the copy. This is the same reason `mirrorCopy` overrides
 *  spin rather than letting `transformToPose` supply it. */
function pasteOnSurface(
  payload: Extract<ClipboardPayload, { kind: 'panel' | 'media' }>,
  frame: SurfaceFrame,
  anchor: PasteAnchor,
  doc: EditorDoc,
  step: number,
): PasteResult {
  const size = payload.footprint;
  const { pose } = transformToPose(frame, anchor.point, payload.entry.rotation, size);
  const wanted = { u: pose.u + step, v: pose.v - step, spin: payload.spin };
  const t = poseToTransform(frame, clampPose(frame, wanted, size, doc), size);

  if (payload.kind === 'panel') {
    return {
      ok: true,
      action: {
        type: 'ADD_PANEL',
        panel: { ...payload.entry, position: t.position, rotation: t.rotation },
      },
    };
  }
  return {
    ok: true,
    action: {
      type: 'ADD_MEDIA',
      media: {
        ...payload.entry,
        position: t.position,
        rotation: t.rotation,
        // A projector is a body AND the screen it throws at. Copying the
        // screen verbatim would put two projectors on one screen, which is
        // not a setup that exists; `screen` is documented as optional
        // precisely so a projector can be a real object before it has been
        // aimed. The paste keeps the device, the user re-aims it.
        screen: payload.entry.kind === 'projector' ? undefined : payload.entry.screen,
      },
    },
  };
}

/** Windows carry their host surface explicitly (unlike panels, whose mounting
 *  is inferred), so the paste re-keys them onto the clicked wall and re-runs
 *  the same overlap rule a drag does. */
function pasteWindow(
  payload: Extract<ClipboardPayload, { kind: 'window' }>,
  frame: SurfaceFrame,
  anchor: PasteAnchor,
  doc: EditorDoc,
  step: number,
): PasteResult {
  const rel = new THREE.Vector3(anchor.point[0], anchor.point[1], anchor.point[2])
    .sub(frame.origin);
  const seated = clampWindowOpening(frame, {
    ...payload.entry,
    surface: surfaceKey(frame.ref),
    u: rel.dot(frame.u) + step,
    v: rel.dot(frame.v) - step,
    // A polygon is an outline in the SOURCE wall's (u, v) frame; carrying it
    // across would re-anchor the shape to the old wall's origin. Rectangular
    // fallback from w/h is the honest paste until an outline-relative
    // translate exists.
    polygon: undefined,
  });
  const others = (doc.windows ?? []).filter((w) => w.surface === seated.surface);
  if (others.some((o) => openingsOverlap(o, seated))) return refuse('overlap');
  return { ok: true, action: { type: 'ADD_WINDOW', window: seated } };
}

/** Free-standing objects: land at the anchor's ground position, footprint
 *  clamped inside the room polygon. A wall anchor's XZ sits ON the polygon
 *  boundary, so the clamp pushes the object exactly one half-extent inside —
 *  which reads as "against the wall you clicked". */
function pasteOnGround(
  payload: ClipboardPayload,
  anchor: PasteAnchor,
  doc: EditorDoc,
  step: number,
): PasteResult {
  const size = payload.footprint;
  const rotation = groundRotationOf(payload);
  const { hx, hz } = footprintHalfXZ(
    size, new THREE.Euler(rotation[0], rotation[1], rotation[2], 'XYZ'),
  );
  const { x, z } = clampBoxToRoom(
    anchor.point[0] + step, anchor.point[2] + step, hx, hz, getRoomPolygon(doc),
  );

  switch (payload.kind) {
    case 'furniture':
      return {
        ok: true,
        action: {
          type: 'ADD_FURNITURE',
          // Furniture sits ON the floor — its height is its footprint, never
          // whatever Y the source happened to have. Matches `clampY`.
          furniture: { ...payload.entry, position: [x, size.h / 2, z] },
        },
      };
    case 'speaker': {
      const position: Vec3 = [x, clampHeight(payload.entry.position[1], doc), z];
      return {
        ok: true,
        action: {
          type: 'ADD_SPEAKER',
          subkind: payload.entry.subkind,
          position,
          // ADD_SPEAKER does not run SYNC_TRANSFORM's auto-face branch, so a
          // speaker that is supposed to track the listener has to be aimed
          // here or it arrives pointing wherever the original did.
          rotationY: payload.entry.faceListener === false
            ? payload.entry.rotationY
            : faceToward(position, doc.listener?.position),
          color: payload.entry.color,
          freqResponse: payload.entry.freqResponse,
          faceListener: payload.entry.faceListener,
        },
      };
    }
    case 'panel':
      return {
        ok: true,
        action: {
          type: 'ADD_PANEL',
          panel: {
            ...payload.entry,
            position: [x, clampHeight(payload.entry.position[1], doc), z],
          },
        },
      };
    case 'media':
      return {
        ok: true,
        action: {
          type: 'ADD_MEDIA',
          media: {
            ...payload.entry,
            position: [x, clampHeight(payload.entry.position[1], doc), z],
            screen: payload.entry.kind === 'projector' ? undefined : payload.entry.screen,
          },
        },
      };
    default:
      return refuse('wrong-surface');
  }
}

/** The rotation a free-standing payload will carry, for footprint purposes. */
function groundRotationOf(payload: ClipboardPayload): Vec3 {
  switch (payload.kind) {
    case 'panel':
    case 'media':
    case 'furniture':
      return payload.entry.rotation;
    case 'speaker':
      return [0, payload.entry.rotationY, 0];
    default:
      return [0, 0, 0];
  }
}

function clampHeight(y: number, doc: EditorDoc): number {
  return Math.max(0, Math.min(doc.dims.H, y));
}

/** Build a clipboard payload from what is selected, or null if that object
 *  cannot be copied. The LISTENER is deliberately absent: a room has exactly
 *  one seat, which is why Ctrl+D excludes it too.
 *
 *  `footprint` is supplied by the caller because a speaker's is a rendering
 *  constant this package cannot see — see ClipboardPayload. */
export function clipboardPayloadFor(
  doc: EditorDoc, id: string, footprint: PanelSize,
): ClipboardPayload | null {
  const panel = doc.panels.find((p) => p.id === id);
  if (panel) {
    return {
      kind: 'panel',
      entry: withoutId(panel),
      footprint,
      spin: surfaceSpinOf(doc, panel),
    };
  }
  const media = (doc.media ?? []).find((m) => m.id === id);
  if (media) {
    return {
      kind: 'media',
      entry: withoutId(media),
      footprint,
      spin: surfaceSpinOf(doc, mediaAsMountable(media)),
    };
  }
  const furniture = doc.furniture.find((f) => f.id === id);
  if (furniture) return { kind: 'furniture', entry: withoutId(furniture), footprint };

  const speaker = doc.speakers.find((s) => s.id === id);
  if (speaker) return { kind: 'speaker', entry: withoutId(speaker), footprint };

  const opening = (doc.windows ?? []).find((w) => w.id === id);
  if (opening) return { kind: 'window', entry: withoutId(opening), footprint };

  return null;
}

/** Every field of an entry except its id. A shallow copy + delete rather than
 *  a `{ id, ...rest }` destructure so the discarded binding needs no lint
 *  suppression — same helper shape `useEditorShortcuts` already uses. */
function withoutId<T extends { id: string }>(entry: T): Omit<T, 'id'> {
  const rest: Partial<T> = { ...entry };
  delete rest.id;
  return rest as Omit<T, 'id'>;
}

/** A mounted object's in-plane rotation on its own host surface, captured at
 *  COPY time — by paste time the original may be gone, or the document may be
 *  a different Design entirely. */
function surfaceSpinOf(doc: EditorDoc, panel: PanelEntry): number {
  if (panel.detached) return 0;
  const frame = findHostFrame(roomSurfaces(doc), panel);
  if (!frame) return 0;
  return transformToPose(frame, panel.position, panel.rotation, panel.size).pose.spin;
}

/** The surface this object is flush against. Proximity along the normal only,
 *  the way `attach()` picks a surface — the object is already seated, so
 *  there is no facing ambiguity to break, and corner-vs-flat is settled by
 *  shape rather than by score. */
function findHostFrame(surfaces: SurfaceFrame[], panel: PanelEntry): SurfaceFrame | null {
  const wantCorner = panel.shape === 'corner';
  let best: SurfaceFrame | null = null;
  let bestOffset = Infinity;
  for (const f of surfaces) {
    if ((f.ref.kind === 'corner') !== wantCorner) continue;
    const { offset } = transformToPose(f, panel.position, panel.rotation, panel.size);
    if (Math.abs(offset) < bestOffset) {
      bestOffset = Math.abs(offset);
      best = f;
    }
  }
  return best;
}

/**
 * The anchor recorded by clicking an OBJECT rather than bare surface.
 *
 * Without this, copy-then-paste with no intervening wall click pastes at
 * whatever surface the user last happened to touch — possibly a different
 * wall, possibly minutes ago. Since scene objects call `stopPropagation` on
 * pointerdown, a click on a wall-mounted panel never reaches the wall's own
 * pick handler, so the anchor has to be derived from the object instead.
 *
 * A mounted object anchors to ITS HOST SURFACE, so "click the panel, Ctrl+C,
 * Ctrl+V" puts the copy on the same wall beside it. Everything free-standing
 * anchors to the floor, which is the surface it actually sits on.
 */
export function anchorForObject(doc: EditorDoc, id: string, point: Vec3): PasteAnchor | null {
  const surfaces = roomSurfaces(doc);
  const floor = surfaces.find((s) => s.ref.kind === 'floor');
  const ground: PasteAnchor | null = floor ? { ref: floor.ref, point } : null;

  const panel = doc.panels.find((p) => p.id === id);
  if (panel) {
    if (panel.detached) return ground;
    const frame = findHostFrame(surfaces, panel);
    return frame ? { ref: frame.ref, point } : ground;
  }

  const media = (doc.media ?? []).find((m) => m.id === id);
  if (media) {
    // `mediaHostSurface`, not `findHostFrame`: a projector is yawed to throw
    // at its screen, so inferring its mount from its own facing lands on the
    // wall it points at — the one surface it is definitely not attached to.
    const frame = mediaHostSurface(media, surfaces);
    return frame ? { ref: frame.ref, point } : ground;
  }

  const opening = (doc.windows ?? []).find((w) => w.id === id);
  if (opening) {
    const ref = parseSurfaceKey(opening.surface);
    return ref ? { ref, point } : ground;
  }

  // Furniture, speakers and the listener are free bodies on the floor.
  return ground;
}
